import { DestroyRef, inject, Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { debounceTime, filter, merge, Observable, switchMap, tap } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DEBOUNCE_CONFIG } from '@app/config/DebounceConfig'
import { stripPaths } from '@core/utils/form-utils'

/**
 * State saved alongside the forms that is not held in a form, e.g. a list kept in a signal.
 * `changes` must only emit for user edits - never for hydration - since every emission counts as one.
 */
export interface AutosaveSource {
  changes: Observable<unknown>
  snapshot: () => unknown
}

interface AutosaveOptions {
  debounceTime?: number
  extraSources?: AutosaveSource[]
  extraFilter?: () => boolean
  ignoredPaths?: string[]
  onValidityBlockChange?: (invalidForms: FormGroup[]) => void
}

@Injectable({
  providedIn: 'root'
})
export class FormAutosaveService {

  private readonly fallBackDebounceTime

  constructor() {
    this.fallBackDebounceTime = inject(DEBOUNCE_CONFIG)
  }

  /**
   * Observes value changes of a FormGroup and triggers a save callback.
   *
   * @param form The FormGroup to observe.
   * @param saveFn Callback that returns an Observable for the save operation.
   * @param destroyRef The DestroyRef to handle unsubscription (e.g. from the component).
   * @param options Configuration for debounce time and optional extra filters.
   */
  public observe<T>(
    form: FormGroup,
    saveFn: () => Observable<T>,
    destroyRef: DestroyRef,
    options: AutosaveOptions = {}
  ): void {
    this.observeMany([form], saveFn, destroyRef, options)
  }

  /**
   * Observes value changes of several FormGroups and triggers a single consolidated save.
   * All forms must be valid for the save to fire. Emissions within the debounce window are
   * coalesced into one call, which also prevents racing parallel saves when the user edits
   * multiple sections quickly. The combined snapshot is JSON-compared to avoid no-op saves.
   */
  public observeMany<T>(
    forms: FormGroup[],
    saveFn: () => Observable<T>,
    destroyRef: DestroyRef,
    options: AutosaveOptions = {}
  ): void {
    if (forms.length === 0) return

    const dt = options.debounceTime ?? this.fallBackDebounceTime
    const sources = forms.flatMap(f => [f.valueChanges, f.statusChanges])
    const ignoredPaths = options.ignoredPaths ?? []
    const extraSources = options.extraSources ?? []

    const buildKey = (): string => JSON.stringify([
      ...forms.map(f => {
        const snapshot = f.getRawValue() as Record<string, unknown>
        return stripPaths(snapshot, ignoredPaths)
      }),
      ...extraSources.map(source => source.snapshot())
    ])

    let lastKey = buildKey()
    let blockingForms: FormGroup[] = []
    // An extra-source edit leaves every form pristine, so it has to hold off the re-baseline below
    // until it is saved - otherwise the next pristine form emission would swallow it.
    let pendingExtraEdit = false

    const formEdits$ = merge(...sources).pipe(
      // Refresh the baseline while all forms are still pristine. `patchValue` from server hydration
      // emits a `valueChanges` event but keeps the form pristine — without this re-baseline, the
      // very first user edit would always diverge from the initial empty-form key and trigger a PUT,
      // even when the change is on an ignored path (e.g. a pure checkbox toggle).
      tap(() => {
        if (!pendingExtraEdit && forms.every(f => f.pristine)) {
          lastKey = buildKey()
        }
      }),
      // Only react to real user edits: `patchValue` from server hydration keeps the form pristine,
      // so this gate prevents a PUT cascade right after a GET.
      filter(() => forms.some(f => f.dirty))
    )
    const extraEdits$ = merge(...extraSources.map(source => source.changes)).pipe(
      tap(() => { pendingExtraEdit = true })
    )

    merge(formEdits$, extraEdits$).pipe(
      // A single invalid form blocks the whole consolidated save, so report the transition in and
      // out of that state — otherwise the edit is dropped with nothing to show for it.
      tap(() => {
        const invalidForms = forms.filter(f => f.invalid)
        if (invalidForms.length !== blockingForms.length) {
          blockingForms = invalidForms
          options.onValidityBlockChange?.(invalidForms)
        }
      }),
      filter(() => forms.every(f => f.valid || f.disabled)),
      filter(() => options.extraFilter ? options.extraFilter() : true),
      debounceTime(dt),
      filter(() => {
        pendingExtraEdit = false
        const key = buildKey()
        if (key === lastKey) return false
        lastKey = key
        return true
      }),
      switchMap(() => saveFn()),
      takeUntilDestroyed(destroyRef)
    ).subscribe()
  }
}
